import { scaleAnimationDurations } from '@/_pure/animationDurations';
import { PRIMITIVE_PRESENTATION_DEFAULTS } from '@/adapters/primitive/_data/config/fragments/_@shared/presentationDefaults';
import { PRIMITIVE_CAMERA_DEFAULTS } from '@/adapters/primitive/_data/config/fragments/camera';
import { SETTLEMENT_PREVIEW } from '@/adapters/primitive/_data/config/fragments/settlementPreview';
import type { AuthoredConfig, Config } from '@/adapters/primitive/_contract/config';
import { buildPrimitiveConfig } from '@/adapters/primitive/_pure/config/config';

export const AUTHORED_PRIMITIVE_CONFIG = {
    base: {
        camera: PRIMITIVE_CAMERA_DEFAULTS.base,
        display: {
            emphasis: {
                color: PRIMITIVE_PRESENTATION_DEFAULTS.colors.base.emphasis.color,
                marks: { whole: ['contour', 'pointer'], band: ['border'], cells: ['border'] },
                showCaptions: false,
                pointerPlacement: { focus: 'stage', depth: 0.5, lift: 0.3 },
            },
            wheel: {
                innerRadius: 0.2,
                ringWidth: 1.05,
                arcSegments: 28,
                wheelGap: 12,
                standoffNearOffset: 1.75,
                standoffFarGap: 1.75,
            },
            cell: {
                countedColor: PRIMITIVE_PRESENTATION_DEFAULTS.colors.base.cell.counted,
                uncountedColor: PRIMITIVE_PRESENTATION_DEFAULTS.colors.base.cell.uncounted,
                countedOpacity: 1,
                uncountedOpacity: 1,
                flightUncountedOpacity: 1,
            },
            placeholder: {
                outlineColor: PRIMITIVE_PRESENTATION_DEFAULTS.colors.base.placeholder.outline,
                outlineOpacity: 1,
                outlineWidth: 1,
            },
            label: {
                digitLabelColor: PRIMITIVE_PRESENTATION_DEFAULTS.colors.base.label.text,
                wheelTitleLabelOpacity: 1,
                wheelTitleRadialOffset: 5.2,
                wheelTitleBackgroundOpacity: 0.85,
                wheelTitleFontSize: 0.24,
                wheelTitleFlagClearanceFontRatio: 0.05,
                wheelTitleFlagMinTextPixels: 1,
                wheelTitlePadding: 0.12,
                backgroundColor: PRIMITIVE_PRESENTATION_DEFAULTS.colors.base.backgroundColor,
            },
            visibility: {
                wheelTitleLabelsVisible: true,
            },
        },
        animation: {
            flightBaseDurationMs: 100,
            flightPerUnitDurationMs: 35,
            rotateBaseDurationMs: 120,
            rotatePerSlotDurationMs: 500,
            crossingLeadSlots: 0,
            fadeDurationMs: 240,
            assemblyHintHoldDurationMs: 500,
            instantDelayMs: 0,
            rewindRate: 3,
        },
    },
    boolean: {
        camera: PRIMITIVE_CAMERA_DEFAULTS.boolean,
        display: {
            decisiveOutline: { color: PRIMITIVE_PRESENTATION_DEFAULTS.colors.boolean.decisiveOutline.color, width: 1 },
            layout: { operandSpreadAngleDegrees: 42.20286299262622, resultChannelDepth: 17.383586362043054, branchHeight: 3 },
            splitMarker: { color: PRIMITIVE_PRESENTATION_DEFAULTS.colors.base.placeholder.outline, halfWidth: 0.3 },
            track: {
                halfGap: 0.12,
                lipThickness: 0.15, lipHeight: 0.11, floorDepth: 0,
                barHeight: 0.075, wheelEndGap: 0.48, floorColor: '#272b30',
            },
        },
        animation: { arrivalDurationMs: 300, pivotDurationMs: 520, pivotSegments: 12, matchDurationMs: 300 },
    },
    natural: {
        camera: PRIMITIVE_CAMERA_DEFAULTS.natural,
        /** Position 0 is a rendered-but-invisible slot, so the ring is 10-fold and matches base-10 rotation. */
        radix: 10,
        operandSchedulingMode: 'interleaved',
        powers: [0, 1, 2],
        digits: [1, 2, 3, 4, 5, 6, 7, 8, 9],
        runawayBudget: {
            borrowAssemblies: 9,
            divisionCycles: 1001,
        },
        display: {
            wheel: {
                ringGap: 0.48,
                angularGapFraction: 0.08,
            },
            band: {
                spentOpacity: 0.2,
            },
            cell: {
                overhangColor: PRIMITIVE_PRESENTATION_DEFAULTS.colors.natural.cell.overhang,
                deficitMarkerColor: PRIMITIVE_PRESENTATION_DEFAULTS.colors.natural.cell.deficitMarker,
                zeroSlotColor: PRIMITIVE_PRESENTATION_DEFAULTS.colors.natural.cell.zero,
                zeroSlotOpacity: 0,
                comparisonDecisiveCellColor: PRIMITIVE_PRESENTATION_DEFAULTS.colors.natural.comparison.decisiveCell,
                relief: {
                    enabled: false,
                    thickness: 0.5,
                    emptyThicknessScale: 0.1,
                },
            },
            settlementPreview: SETTLEMENT_PREVIEW,
            comparison: {
                rayColor: PRIMITIVE_PRESENTATION_DEFAULTS.colors.natural.comparison.ray,
                rayOpacity: 0.9,
                rayWidth: 1,
                helperCircle: {
                    outlineColor: PRIMITIVE_PRESENTATION_DEFAULTS.colors.natural.comparison.helperCircleOutline,
                    outlineWidth: 1,
                },
            },
            marker: {
                canonicalZero: {
                    visible: true,
                    color: PRIMITIVE_PRESENTATION_DEFAULTS.colors.natural.canonicalZeroMarker.color,
                    opacity: 0.9,
                    radius: 0.12,
                },
                axialTie: {
                    color: PRIMITIVE_PRESENTATION_DEFAULTS.colors.natural.axialTie.color,
                    opacity: 0.0,
                },
                axisRod: {
                    visible: true,
                    color: PRIMITIVE_PRESENTATION_DEFAULTS.colors.natural.axisRod.color,
                    opacity: 0.35,
                    width: 1,
                },
            },
            label: {
                digitLabelsVisible: false, // labels z-fight with the sector faces; re-enable once that is solved
                radialBandLabelsVisible: false,
                radialBandLabelOpacity: 1,
                angularBandLabelsVisible: false,
                angularBandLabelOpacity: 1,
            },
        },
        animation: {
            comparisonSlotDurationMs: 300,
            naturalLessThan: {
                helperEntryDurationMs: 480,
                targetObservationDurationMs: 240,
                sourceObservationDurationMs: 240,
                inspectedCellOpacity: 0.12,
                slotScanDurationMs: 380,
                bandAdvanceDurationMs: 220,
                decisiveCellFlashDurationMs: 500,
                decisionMaterializationDurationMs: 420,
            },
        },
    },
} as const satisfies AuthoredConfig;

export const UNSCALED_PRIMITIVE_CONFIG = buildPrimitiveConfig(AUTHORED_PRIMITIVE_CONFIG);

export const CONFIG = scaleAnimationDurations<Config>(
    UNSCALED_PRIMITIVE_CONFIG,
    PRIMITIVE_PRESENTATION_DEFAULTS.animationDurationMultiplier,
);